import type { FigurePagePreferenceModel } from "@patent-studio/api-types";
import type { FigureLayout } from "./figure-labels";

/**
 * Pack figures onto drawing sheets the way the filed draft will.
 *
 * Two standalone portraits share a sheet in set order. A group keeps its views
 * together on sheets of its own: in rows when it has one or two, two across and
 * two down when it has three or four, over consecutive sheets of four when it
 * has more. A figure marked own-page stands alone and the pairing starts over
 * after it. A landscape figure always stands alone, rotated:
 * landscape-implies-own-page is this projection's rule, held here rather than
 * stored so it cannot be stored inconsistently.
 */

export interface SheetFigure {
  fileId: string;
  landscape: boolean;
}

export interface Sheet {
  figures: SheetFigure[];
  /** Two for a group's three or four views set two across; one otherwise. */
  columns: 1 | 2;
}

const FIGURES_PER_SHEET = 2;
const VIEWS_PER_GRID = 4;

/** What a figure with no stored preference gets: a portrait that may share. */
export const DEFAULT_PAGE_PREFERENCE: FigurePagePreferenceModel = {
  orientation: "portrait",
  own_page: false,
};

/** Landscape-implies-own-page lives here, so no caller restates it. */
export function standsAlone(preference: FigurePagePreferenceModel): boolean {
  return preference.orientation === "landscape" || preference.own_page;
}

/** A group's run of shared views on sheets of its own, in fours. */
function keptTogether(views: SheetFigure[]): Sheet[] {
  const sheets: Sheet[] = [];
  for (let at = 0; at < views.length; at += VIEWS_PER_GRID) {
    const figures = views.slice(at, at + VIEWS_PER_GRID);
    sheets.push({
      figures,
      columns: figures.length > FIGURES_PER_SHEET ? 2 : 1,
    });
  }
  return sheets;
}

export function packSheets(
  entries: FigureLayout["entries"],
  preferences: Record<string, FigurePagePreferenceModel>,
): Sheet[] {
  const sheets: Sheet[] = [];
  // A Map read answers honestly for a figure with no stored preference, where
  // an index read types as always present.
  const stored = new Map(Object.entries(preferences));
  /** The sheet a figure takes by itself, or undefined for one that shares. */
  const alone = (fileId: string): Sheet | undefined => {
    const preference = stored.get(fileId) ?? DEFAULT_PAGE_PREFERENCE;
    if (!standsAlone(preference)) return undefined;
    const landscape = preference.orientation === "landscape";
    return { figures: [{ fileId, landscape }], columns: 1 };
  };
  let open: Sheet | undefined;

  for (const entry of entries ?? []) {
    if (entry.kind === "group") {
      const members = entry.members ?? [];
      if (members.length === 0) continue;
      // A group starts a sheet of its own, and what follows it starts another.
      open = undefined;
      let run: SheetFigure[] = [];
      for (const member of members) {
        const own = alone(member.file_id);
        if (own === undefined) {
          run.push({ fileId: member.file_id, landscape: false });
          continue;
        }
        sheets.push(...keptTogether(run), own);
        run = [];
      }
      sheets.push(...keptTogether(run));
      continue;
    }
    const own = alone(entry.file_id);
    if (own !== undefined) {
      sheets.push(own);
      open = undefined;
      continue;
    }
    if (open === undefined) {
      open = { figures: [], columns: 1 };
      sheets.push(open);
    }
    open.figures.push({ fileId: entry.file_id, landscape: false });
    if (open.figures.length >= FIGURES_PER_SHEET) open = undefined;
  }
  return sheets;
}

/**
 * The drawing sheet as the Drawings PDF prints it and the preview draws it, in
 * centimetres: US Letter, as the draft is, inside the 37 CFR 1.84(g) margins.
 * Inside the margins a band at the top carries the sheet number; the rest holds
 * the figures in rows a gap apart, each above a band for its caption.
 */
export const SHEET = {
  page: { width: 21.59, height: 27.94 },
  margin: { top: 2.5, right: 1.5, bottom: 1, left: 2.5 },
  band: { number: 1, caption: 1, gap: 0.5, column: 0.5 },
} as const;

/** The area inside the margins, which the number band and the figures share. */
export const USABLE = {
  width: SHEET.page.width - SHEET.margin.left - SHEET.margin.right,
  height: SHEET.page.height - SHEET.margin.top - SHEET.margin.bottom,
};

/**
 * The box a drawing fits at its aspect ratio: alone on its sheet, one of two,
 * or alone and turned a quarter, whose width then runs up the sheet. Each line
 * set under it (its caption, and the legend of a prior art figure) takes a
 * caption band; a sole figure in the matter carries no caption.
 */
export type Bounds =
  "whole-sheet" | "half-sheet" | "quarter-sheet" | "whole-sheet-turned";

export function drawingBox(
  bounds: Bounds,
  lines: number,
): { width: number; height: number } {
  const { number, caption, gap } = SHEET.band;
  const rest = USABLE.height - number;
  const band = caption * lines;
  if (bounds === "whole-sheet-turned") {
    return { width: rest, height: USABLE.width - band };
  }
  const rows = bounds === "whole-sheet" ? 1 : 2;
  const columns = bounds === "quarter-sheet" ? 2 : 1;
  return {
    width: (USABLE.width - SHEET.band.column * (columns - 1)) / columns,
    height: (rest - gap * (rows - 1)) / rows - band,
  };
}
